/**
 * Quiz New - Split Test Variation
 * 8-step quiz with conditional symptoms based on wellness goal selection
 * Steps: 1-Goal, 2-Symptoms, 3-Duration, 4-Impact, 5-Energy, 6-Sleep, 7-Confirmation, 8-Results
 */

const QUIZ_DATABASE_NEW = {
  'cat_blood_sugar': {
    bundle: 'blood-sugar-bundle',
    bundleTitle: 'Blood Sugar Bundle — Complete Glucose System',
    result: 'Your answers suggest your body may be dealing with glucose instability — leading to cravings, low energy, or metabolism slowdown.',
    reason: 'This bundle supports healthy blood sugar levels, stabilizes energy, and promotes healthier metabolism throughout the day.',
    symptoms: {
      'Sugar cravings': ['berberine', 'blood-sugar-bundle', 'blood-sugar-support-supplement'],
      'Afternoon crashes': ['berberine', 'blood-sugar-bundle'],
      'Difficulty losing weight': ['blood-sugar-support-supplement', 'blood-sugar-bundle'],
      'Feeling hungry soon after meals': ['berberine', 'blood-sugar-support-supplement'],
      'Low or inconsistent energy': ['blood-sugar-bundle', 'blood-sugar-support-supplement'],
      'Brain fog after eating': ['berberine', 'blood-sugar-bundle', 'magnesium-glycinate'],
    }
  },
  'cat_heart': {
    bundle: 'heart-health',
    bundleTitle: 'Heart Health Bundle — Cardiovascular Support',
    result: 'Your answers point toward circulation, cholesterol, or energy concerns.',
    reason: 'Supports cholesterol, circulation, and heart energy — a complete cardiovascular approach.',
    symptoms: {
      'High cholesterol indicators': ['citrus-bergamot', 'heart-health', 'fish-oil'],
      'Cold hands or feet': ['heart-health', 'coq10', 'vitamin-d3-k2'],
      'Low physical stamina': ['coq10', 'heart-health'],
      'Family heart history': ['coq10', 'heart-health', 'fish-oil'],
      'Poor circulation': ['heart-health', 'vitamin-d3-k2', 'fish-oil'],
      'Easily fatigued': ['coq10', 'heart-health'],
    }
  },
  'cat_joint': {
    bundle: 'joint-health',
    bundleTitle: 'Joint Health — Total Mobility Solution',
    result: 'Your symptoms show signs of inflammation or mobility limitations.',
    reason: 'Supports flexibility, reduces inflammation, and promotes smoother movement.',
    symptoms: {
      'Joint stiffness': ['turmeric-curcumin', 'joint-health', 'glucosamine-chondroitin-msm'],
      'Morning aches': ['turmeric-curcumin', 'joint-health'],
      'Limited mobility': ['glucosamine-chondroitin-msm', 'joint-health'],
      'Exercise discomfort': ['turmeric-curcumin', 'joint-health'],
      'Chronic joint pain': ['turmeric-curcumin', 'glucosamine-chondroitin-msm', 'joint-health'],
      'Swelling in joints': ['turmeric-curcumin', 'joint-health'],
    }
  },
  'cat_sleep': {
    bundle: 'mood-sleep',
    bundleTitle: 'Sleep & Mood Reset Bundle',
    result: 'Your answers suggest your sleep, stress, or mood may need support.',
    reason: 'Promotes deeper sleep, calmer evenings, and more balanced emotional wellbeing.',
    symptoms: {
      'Trouble falling asleep': ['magnesium-glycinate'],
      'Waking up at night': ['magnesium-glycinate', 'cortisol-care'],
      'Feeling anxious': ['saffron-extract', 'cortisol-care', 'magnesium-glycinate'],
      'Low mood': ['saffron-extract', 'magnesium-glycinate'],
      'Stress and tension': ['cortisol-care', 'magnesium-glycinate'],
      'Mental fatigue': ['saffron-extract', 'magnesium-glycinate'],
    }
  },
  'cat_immune': {
    bundle: 'immune-system',
    bundleTitle: 'Immune System — Triple-Defense Shield',
    result: 'Your symptoms suggest your immune defenses may need additional support.',
    reason: 'Combines antioxidants and immune-supporting nutrients for daily defense and recovery.',
    symptoms: {
      'Frequent colds or illness': ['quercetin', 'immune-system'],
      'Slow recovery': ['glutathione', 'immune-system'],
      'Seasonal allergies': ['quercetin', 'immune-system'],
      'Low energy levels': ['immune-system', 'coq10'],
      'Chronic inflammation': ['quercetin', 'turmeric-curcumin'],
      'Weak immune response': ['immune-system', 'quercetin', 'glutathione'],
    }
  },
  'cat_longevity': {
    bundle: 'longevity',
    bundleTitle: 'LONGEVITY — Healthy Aging & Cellular Energy Protocol',
    result: 'Your answers point toward cellular energy, recovery, and healthy aging.',
    reason: 'This protocol helps support cellular repair, energy production, and healthy aging. It targets the root causes of fatigue, inflammation, and reduced vitality — helping your body perform and recover like it used to.',
    symptoms: {
      'Signs of premature aging': ['resveratrol', 'glutathione'],
      'Memory concerns': ['resveratrol', 'coq10'],
      'Low antioxidant intake': ['glutathione', 'resveratrol', 'quercetin'],
      'Cellular fatigue': ['resveratrol', 'coq10'],
      'Oxidative stress': ['glutathione', 'resveratrol'],
      'Skin health decline': ['resveratrol', 'glutathione'],
    }
  },
  'cat_digest': {
    bundle: 'digestion',
    bundleTitle: 'Gut Health Protocol — Digestive Reset System',
    result: 'Your answers suggest your digestion and gut balance may need support.',
    reason: 'This protocol is designed to support gut lining integrity, microbiome balance, and comfortable digestion. A balanced gut also supports immunity, mood, and nutrient absorption — making this a powerful whole-body approach.',
    symptoms: {
      'Bloating': ['probiotic'],
      'Irregular digestion': ['probiotic'],
      'Gut discomfort': ['probiotic'],
      'Food sensitivities': ['probiotic'],
      'Gas or cramping': ['probiotic'],
      'Low gut bacteria': ['probiotic', 'womens-probiotic'],
    }
  }
};

class QuizNew {
  constructor(form, db) {
    this.form = form;
    this.steps = Array.from(form.querySelectorAll('.quiz-new__step'));
    this.totalSteps = this.steps.length;
    this.db = db;

    this.state = {
      answers: {
        stepOne: { wellness_goal: '' },
        stepTwo: { symptoms: [] },
        stepThree: { duration: '' },
        stepFour: { impact: '' },
        stepFive: { energy: '' },
        stepSix: { sleep_quality: '' },
      },
      currentStep: 1,
      results: []
    };

    this._bindSubmitClick();
    this._bindGoalChange();
    this._bindConfirmationClick();
    this._goToStep(1);

    // Track quiz page view
    this._trackEvent('quiz_new_page_view');
  }

  _bindSubmitClick() {
    const btn = this.form.querySelector('.quiz-new__submit');
    if (!btn) return;
    btn.addEventListener('click', e => {
      e.preventDefault();

      if (!this._validateCurrentStep()) return;

      // Step 7 (confirmation) - show results
      if (this.state.currentStep === 7) {
        this._showResults();
        return;
      }

      // Steps 1-6 use the Continue button
      if (this.state.currentStep < 7) {
        this._goToStep(this.state.currentStep + 1);
        return;
      }
    });
  }

  _updateFooterButton() {
    const btn = this.form.querySelector('.quiz-new__submit');
    if (!btn) return;

    if (this.state.currentStep === 7) {
      btn.textContent = 'Show My Personalized Plan';
    } else {
      btn.textContent = 'Continue';
    }
  }

  _bindGoalChange() {
    const inputs = Array.from(this.form.querySelectorAll('[name="wellness_goal"]'));
    inputs.forEach(input => {
      input.addEventListener('change', () => {
        this._showSymptomsForCategory(input.value);
      });
    });
  }

  _showSymptomsForCategory(category) {
    // Hide all symptom containers
    const containers = this.form.querySelectorAll('.quiz-new__symptoms-container');
    containers.forEach(c => c.style.display = 'none');

    // Show the selected category's symptoms
    const selectedContainer = this.form.querySelector(
      `.quiz-new__symptoms-container[data-category="${category}"]`
    );
    if (selectedContainer) {
      selectedContainer.style.display = 'block';
    }

    // Clear previously selected symptoms when category changes
    const checkboxes = this.form.querySelectorAll('[name="symptoms"]');
    checkboxes.forEach(cb => cb.checked = false);
  }

  _validateCurrentStep() {
    const { currentStep } = this.state;
    this._clearErrors();
    const errors = [];

    const showError = (input, message) => {
      if (!input) return;
      const error = document.createElement('span');
      error.className = 'quiz-new__error';
      error.textContent = message;
      const fieldWrapper = input.closest('.quiz-new__field') || input.parentElement;
      input.classList.add('quiz-new__input-error');
      fieldWrapper.appendChild(error);
      errors.push(input);
    };

    const validators = {
      1: () => {
        const goal = this.form.querySelector('[name="wellness_goal"]:checked');
        if (!goal) showError(this.form.querySelector('[name="wellness_goal"]'), 'Please select a wellness goal.');
      },
      2: () => {
        // Only validate visible symptoms (from the selected category)
        const selectedGoal = this.state.answers.stepOne.wellness_goal;
        const visibleContainer = this.form.querySelector(`.quiz-new__symptoms-container[data-category="${selectedGoal}"]`);
        if (visibleContainer) {
          const symptoms = visibleContainer.querySelectorAll('[name="symptoms"]:checked');
          if (symptoms.length === 0) {
            showError(visibleContainer.querySelector('[name="symptoms"]'), 'Please select at least one symptom.');
          }
        }
      },
      3: () => {
        const duration = this.form.querySelector('[name="duration"]:checked');
        if (!duration) showError(this.form.querySelector('[name="duration"]'), 'Please select a duration.');
      },
      4: () => {
        const impact = this.form.querySelector('[name="impact"]:checked');
        if (!impact) showError(this.form.querySelector('[name="impact"]'), 'Please select an option.');
      },
      5: () => {
        const energy = this.form.querySelector('[name="energy"]:checked');
        if (!energy) showError(this.form.querySelector('[name="energy"]'), 'Please select an option.');
      },
      6: () => {
        const sleep = this.form.querySelector('[name="sleep_quality"]:checked');
        if (!sleep) showError(this.form.querySelector('[name="sleep_quality"]'), 'Please select an option.');
      }
    };

    validators[currentStep] && validators[currentStep]();

    if (errors.length > 0) {
      errors[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
    return true;
  }

  _clearErrors() {
    this.form.querySelectorAll('.quiz-new__error').forEach(el => el.remove());
    this.form.querySelectorAll('.quiz-new__input-error').forEach(el => {
      el.classList.remove('quiz-new__input-error');
    });
  }

  _saveCurrentStep() {
    const { currentStep, answers } = this.state;
    const fd = new FormData(this.form);

    switch (currentStep) {
      case 1:
        answers.stepOne.wellness_goal = fd.get('wellness_goal') || '';
        this._trackEvent('quiz_new_step_1_complete', {
          answer: answers.stepOne.wellness_goal
        });
        break;
      case 2:
        answers.stepTwo.symptoms = fd.getAll('symptoms');
        this._trackEvent('quiz_new_step_2_complete', {
          answer: answers.stepTwo.symptoms.join(', ')
        });
        break;
      case 3:
        answers.stepThree.duration = fd.get('duration') || '';
        this._trackEvent('quiz_new_step_3_complete', {
          answer: answers.stepThree.duration
        });
        break;
      case 4:
        answers.stepFour.impact = fd.get('impact') || '';
        this._trackEvent('quiz_new_step_4_complete', {
          answer: answers.stepFour.impact
        });
        break;
      case 5:
        answers.stepFive.energy = fd.get('energy') || '';
        this._trackEvent('quiz_new_step_5_complete', {
          answer: answers.stepFive.energy
        });
        break;
      case 6:
        answers.stepSix.sleep_quality = fd.get('sleep_quality') || '';
        this._trackEvent('quiz_new_step_6_complete', {
          answer: answers.stepSix.sleep_quality
        });
        break;
    }
  }

  _goToStep(n) {
    if (n < 1 || n > this.totalSteps) return;
    this._saveCurrentStep();

    this.steps.forEach(step => step.classList.add('is-quiz-hidden'));
    this.steps[n - 1].classList.remove('is-quiz-hidden');
    this.state.currentStep = n;

    this._updateFooterButton();

    // ADA: announce step change to screen readers and move focus
    var stepEl = this.form.querySelector('[data-step="' + n + '"]');
    if (stepEl) {
      var heading = stepEl.querySelector('h2, h3, h4');
      var stepTitle = heading ? heading.textContent.trim() : '';
      if (window.adaAnnounce) {
        window.adaAnnounce('Step ' + n + ' of ' + this.totalSteps + (stepTitle ? ': ' + stepTitle : ': Question ' + n));
      }
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  _showResults() {
    this._saveCurrentStep();

    const { wellness_goal } = this.state.answers.stepOne;
    const chosenSymptoms = this.state.answers.stepTwo.symptoms;
    const goalData = this.db[wellness_goal] || {};
    const symptomProducts = goalData.symptoms || {};

    chosenSymptoms.forEach(symptom => {
      const prods = symptomProducts[symptom] || [];
      prods.forEach(p => {
        if (!this.state.results.includes(p)) {
          this.state.results.push(p);
        }
      });
    });

    this._goToStep(8);

    const resultsContainer = this.form.querySelector('.quiz-new__step[data-step="8"]');

    const resultElement = resultsContainer.querySelector('.quiz-new__results-intro');
    if (resultElement && goalData.result) {
      resultElement.textContent = goalData.result;
    }

    const detectedList = resultsContainer.querySelector('.quiz-new__detected-list');
    if (detectedList) {
      detectedList.innerHTML = '';
      chosenSymptoms.forEach(symptom => {
        const li = document.createElement('li');
        li.innerHTML = `<span class="quiz-new__x-mark">✕</span> ${symptom}`;
        detectedList.appendChild(li);
      });
    }

    const planTitle = resultsContainer.querySelector('.quiz-new__plan-title');
    if (planTitle && goalData.bundleTitle) {
      planTitle.textContent = goalData.bundleTitle;
    }

    const bundleRecommendations = resultsContainer.querySelectorAll('.quiz-new__bundle-recommendation');
    bundleRecommendations.forEach(rec => rec.classList.add('hidden'));

    const targetBundle = resultsContainer.querySelector(`.quiz-new__bundle-recommendation[data-bundle="${goalData.bundle}"]`);
    if (targetBundle) {
      targetBundle.classList.remove('hidden');
    }

    const reasonEl = resultsContainer.querySelector('.quiz-new__supporting-reason');
    if (reasonEl && goalData.reason) {
      reasonEl.textContent = goalData.reason;
    }

    const bundleProductContainers = resultsContainer.querySelectorAll('.quiz-new__bundle-products');
    bundleProductContainers.forEach(container => container.classList.add('hidden'));

    const targetBundleProducts = resultsContainer.querySelector(
      `.quiz-new__bundle-products[data-bundle-products="${goalData.bundle}"]`
    );
    if (targetBundleProducts) {
      targetBundleProducts.classList.remove('hidden');
    }

    this.form.querySelector('.quiz-new__footer').classList.add('hidden');

    this._trackEvent('quiz_new_completed', {
      recommended_bundle: goalData.bundle
    });
  }

  _trackEvent(eventName, properties = {}) {
    window.igEvents = window.igEvents || [];
    window.igEvents.push({
      event: eventName,
      ...properties
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const quizForm = document.querySelector('.quiz-new__form');
  if (quizForm) {
    new QuizNew(quizForm, QUIZ_DATABASE_NEW);
  }
});